/* eslint-disable react/jsx-no-bind */
//@flow
import * as Immutable from 'immutable';
import React from 'react';
import { withRouter } from 'common/components/core/router';
import { connect } from 'react-redux';
import compose from 'recompose/compose';
import { memoizedImmutable } from 'common/utils/Memoizer';
import { translate } from 'react-i18next';

import { AccessTypeEnum } from 'common/models/internal/MasterPersonnel';
import type { TeamModelType } from 'common/models/flow/Team';
import {
    PersonnelTypeEnum,
    type PersonnelTypeEnumType
} from 'common/enums/PersonnelEnums';

import PersonnelActions from 'common/redux/actions/personnelMaster';

import { createEntityImportContainer } from 'common/entity/generic/EntityImportContainer';
import { EntityImportHelper } from 'common/entity/helpers/EntityImportHelper';
import PersonnelTypeModalSelector from './PersonnelTypeModalSelector';
import PersonnelMasterImportWorker from './workers/PersonnelMasterImportWorker';

import PersonnelImportHelper from './PersonnelImportHelper';
import PERSONNEL_IMPORT_FORMAT from './tableConfig';
import { ENTITY_NAME_ID } from 'common/constants/entity/personnelMaster';

import { isTransporterPersonnelEnabled } from './utils';
import ClientConfigService from 'common/services/ClientConfigService';

const actions = {
    addEntities: PersonnelActions.addEntities
};

const EntityImportContainer = createEntityImportContainer({
    ImportWorker: PersonnelMasterImportWorker
});

type Props = {
    clientId: string,
    entitySettings: Object,
    teams: Immutable.List<TeamModelType>,
    validateEntityIds: Function,
    personnelType: PersonnelTypeEnumType,
    t: string => string
};

class PersonnelMasterImportContainer extends React.PureComponent<Props> {
    @memoizedImmutable()
    getDefaultColumns(entitySettings, teams) {
        const { t } = this.props;
        const customFields = PersonnelImportHelper.getCustomFields(
            entitySettings
        );

        return EntityImportHelper.getDefaultColumns({
            columns: PERSONNEL_IMPORT_FORMAT,
            teams,
            customFields,
            t,
            entityNameId: ENTITY_NAME_ID
        });
    }

    @memoizedImmutable()
    getTemplateRow(entitySettings) {
        const { t, personnelType } = this.props;
        const customFields = PersonnelImportHelper.getCustomFields(
            entitySettings
        );

        return EntityImportHelper.getTemplateRow({
            columns: PERSONNEL_IMPORT_FORMAT,
            customFields,
            t,
            entityNameId: ENTITY_NAME_ID,
            translationKeys: {
                accessType: AccessTypeEnum.getOptions({
                    translationKey: 'masterPersonnelProperties.accessTypes'
                })
                    .map(({ label }) => label)
                    .join('/'),
                personnelType: PersonnelTypeEnum.getOptions({
                    translationKey: 'masterPersonnelProperties.personnelTypes'
                }).find(({ value }) => value === personnelType)?.label
            }
        });
    }

    getActions() {
        const { clientId } = this.props;
        return [
            ClientConfigService.isRbacEnabled()
                ? {
                      label:
                          'entityManagement:masterImportTemplateValues:masterPersonnel:copyRoleIds',
                      onClick: () => {
                          const url = `${window.location.origin}/#/client/${clientId}/settings/roles`;
                          window.open(url, '_blank');
                      }
                  }
                : null
        ].filter(Boolean);
    }
    render() {
        const { entitySettings, teams, personnelType } = this.props;

        return (
            <EntityImportContainer
                {...this.props}
                context={{ personnelType }}
                entityNameId={ENTITY_NAME_ID}
                generateEntityList={PersonnelImportHelper.generateEntityList}
                templateRowData={this.getTemplateRow(entitySettings)}
                defaultColumns={this.getDefaultColumns(entitySettings, teams)}
                sanitizeBeforeUpload
                actions={this.getActions()}
            />
        );
    }
}

const PersonnelMasterImportContainerHOC = compose(
    withRouter,
    connect(null, actions),
    translate('entityManagement')
)(PersonnelMasterImportContainer);

const PersonnelMasterImportContainerWrap = props => {
    const isTransporterEnabled = isTransporterPersonnelEnabled(
        props.entitySettings
    );

    return !isTransporterEnabled ? (
        <PersonnelMasterImportContainerHOC
            {...props}
            personnelType={PersonnelTypeEnum.CLIENT}
        />
    ) : (
        <PersonnelTypeModalSelector
            onDismiss={props.onDismiss}
            render={type => (
                <PersonnelMasterImportContainerHOC
                    {...props}
                    personnelType={type}
                />
            )}
        />
    );
};

export default PersonnelMasterImportContainerWrap;
